/**
 * UserReferenceFieldsSection
 *
 * Renders the per-user reference-field grid inside the profile editor.
 * Each defined field is shown as a labeled text input; values are
 * staged in component state and committed via `onValuesChange` so the
 * parent's Save Changes flow persists them alongside the rest of the
 * profile edits (same UX as the rest of the form — no separate save).
 *
 * Visibility:
 *   - Hidden entirely for learners viewing their own profile. These
 *     are admin-managed integration identifiers, not learner-editable.
 *   - Visible (read-write) for any admin role.
 *   - Schema management ("Manage fields") is gated to account / super
 *     admins, who own tenant-wide identity mapping.
 */

import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Button,
  Grid,
  TextField,
  Tooltip,
  Typography,
} from '../ui';
import { EditIcon } from '../icons';
import {
  selectAllReferenceFields,
  selectReferenceFieldsStatus,
  useEnsureReferenceFields,
} from '../../features/lookups/lookupsSlice';
import ManageReferenceFieldsDialog from './ManageReferenceFieldsDialog';

interface UserReferenceFieldsSectionProps {
  /** The user whose values we're editing. `null`/undefined disables
   *  inputs and shows a placeholder — happens momentarily on first
   *  render before the parent has the user object. */
  userId: number | string | null | undefined;
  /** Form state owned by the parent so reference values can be saved
   *  together with the standard profile fields. */
  values: Record<string, string>;
  onValuesChange: (next: Record<string, string>) => void;
  /** Whether the inputs are editable. False for learners viewing their
   *  own profile. */
  canEdit: boolean;
  /** Whether to surface the schema-management button. Only the highest
   *  roles get this; workspace admins can fill values but not change
   *  the global field list. */
  canManageSchema: boolean;
}

export default function UserReferenceFieldsSection({
  userId,
  values,
  onValuesChange,
  canEdit,
  canManageSchema,
}: UserReferenceFieldsSectionProps) {
  // Tenant-wide definitions from the lookups slice (fetched once).
  useEnsureReferenceFields();
  const orderedDefs = useSelector(selectAllReferenceFields);
  const referenceFieldsStatus = useSelector(selectReferenceFieldsStatus);
  const [manageOpen, setManageOpen] = useState(false);

  const handleChange = (fieldId: string, value: string) => {
    onValuesChange({ ...values, [fieldId]: value });
  };

  // Prune staged values whose field was removed from the schema, so the
  // parent's unsaved state can't resurrect a deleted field on save. Gate on
  // a successful schema load: pruning against an unloaded/failed schema
  // (orderedDefs still []) would treat every saved value as removed and wipe
  // them all — which the parent would then persist on Save.
  useEffect(() => {
    if (referenceFieldsStatus !== 'succeeded') return;
    const validIds = new Set(orderedDefs.map((d) => String(d.id)));
    const pruned: Record<string, string> = {};
    for (const [fid, v] of Object.entries(values)) {
      if (validIds.has(fid)) pruned[fid] = v;
    }
    if (Object.keys(pruned).length !== Object.keys(values).length) {
      onValuesChange(pruned);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderedDefs, referenceFieldsStatus]);

  return (
    <Box>
      {/* Section header removed — the parent SettingsCard now owns the
          title + subtitle. The Manage button stays here as a small
          right-aligned affordance above the field grid; it only
          renders when the viewer can edit the global schema. */}
      {canManageSchema && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <Tooltip title="Add or edit field definitions">
            <span>
              <Button
                size="small"
                variant="outlined"
                color="inherit"
                startIcon={<EditIcon />}
                onClick={() => setManageOpen(true)}
              >
                Manage fields
              </Button>
            </span>
          </Tooltip>
        </Box>
      )}
      {orderedDefs.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No reference fields defined.{' '}
          {canManageSchema ? 'Use "Manage fields" to add one.' : 'Ask an account admin to define one.'}
        </Typography>
      ) : (
        <Grid container spacing={2}>
          {orderedDefs.map((def) => {
            const fieldId = String(def.id);
            return (
              <Grid key={def.id} size={{ xs: 12, sm: 6 }}>
                <TextField
                  label={def.label}
                  value={values[fieldId] ?? ''}
                  onChange={(e) => handleChange(fieldId, e.target.value)}
                  placeholder={def.key}
                  fullWidth
                  disabled={!canEdit || !userId}
                  // The key is the integration-facing identifier; show it
                  // beneath the input so admins editing values can see
                  // which external field this maps to without flipping
                  // to the Manage dialog.
                  helperText={`Key: ${def.key}`}
                />
              </Grid>
            );
          })}
        </Grid>
      )}

      <ManageReferenceFieldsDialog
        open={manageOpen}
        onClose={() => setManageOpen(false)}
        // Definitions are reactive (lookups slice) and staged values are
        // pruned by the effect above, so nothing to do here on change.
        onSchemaChanged={() => {}}
      />
    </Box>
  );
}
